import React, { useEffect, useRef } from "react";
import { useAddonsManager } from "./providers/AddonsManagerProvider";
import { AddonParams } from "./base/BaseAddon";
import { EntityRelationValidationAddon } from "./EntityRelationAddons/EntityRelationValidation/EntityRelationValidationAddon";
import { AddonConstructor, EntryAddonConstructor, IAddonRegistry, resolveAddonDependencies } from "./AddonRegistry";
import { createEntityRegistry } from "./EntityRelationAddons/RegisterEntityAddons";
import { EntityRelationAddon } from "./EntityRelationAddons/EntityRelationAddon";

interface AddonsInitializerProps {
  params: AddonParams;
  addons: AddonConstructor[];
  addonRegistry: IAddonRegistry;
}

const AddonsInitializer: React.FC<AddonsInitializerProps> = ({ params, addons, addonRegistry }) => {
  const { registerAddon, clearAddons, handleStepChange, goBack } = useAddonsManager();
  const registeredAddonsRef = useRef<boolean>(false);

  useEffect(() => {
    if (registeredAddonsRef.current) return;

    clearAddons();

    const resolvedAddonClasses = resolveAddonDependencies(addons, addonRegistry);

    const hasEntityRelationFields = params.fields.some(field => field.type === "entityRelation");

    const entityAddons: EntityRelationAddon[] = [];

    if (hasEntityRelationFields) {
      // check which entity addons are included in the resolved classes
      resolvedAddonClasses.forEach(AddonClass => {
        if (isEntityAddon(AddonClass) && AddonClass !== EntityRelationValidationAddon) {
          const addon = new AddonClass(handleStepChange, goBack, params);
          entityAddons.push(addon as EntityRelationAddon);
        }
      });
    }

    const addonInstances = resolvedAddonClasses
      .map(AddonClass => {
        if (AddonClass === EntityRelationValidationAddon) {
          return null;
        }

        if (isEntityAddon(AddonClass)) {
          return null;
        }

        let addon;
        if (AddonClass === addonRegistry.uploadAddon) {
          const EntryAddon = AddonClass as EntryAddonConstructor;
          addon = new EntryAddon(handleStepChange, params);
        } else {
          addon = new AddonClass(handleStepChange, goBack, params);
        }

        return addon;
      })
      .filter(addon => addon !== null);

    addonInstances.push(...entityAddons);

    // hadnle entity relation validtion
    if (hasEntityRelationFields && entityAddons.length > 0) {
      const entityValidationAddon = new EntityRelationValidationAddon(handleStepChange, goBack, {
        ...params,
        entityRegistry: createEntityRegistry(),
        entityRelationAddons: entityAddons
      });

      addonInstances.push(entityValidationAddon);
    }

    const validateAddonIndex = addonInstances.findIndex(addon => addon.name === "ValidateDataAddon");
    if (validateAddonIndex !== -1) {
      const ValidateAddonClass = resolvedAddonClasses.find(
        AddonClass => new AddonClass(handleStepChange, goBack, params).name === "ValidateDataAddon"
      );

      if (ValidateAddonClass) {
        addonInstances[validateAddonIndex] = new ValidateAddonClass(handleStepChange, goBack, {
          ...params,
          registeredAddons: addonInstances.filter(a => a !== null)
        });
      }
    }

    addonInstances.forEach(addon => {
      if (addon) registerAddon(addon);
    });

    registeredAddonsRef.current = true;
    // eslint-disable-next-line
  }, []);

  return null;
};

function isEntityAddon(AddonClass: AddonConstructor): boolean {
  return (AddonClass as any).metadata !== undefined;
}

export default AddonsInitializer;
